import React, { useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Loader from "../../common/loader";
import {
  addCoupon,
  deleteCoupon,
  fetchCouponList,
  updateCoupon,
  updateCouponStatus,
} from "../../redux/adminReducer";
import { FaRegEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import Modal from "../../common/modal";
import { toast } from "react-toastify";
import { set, useForm } from "react-hook-form";

function Coupons() {
  const { lang, translations, dir } = useSelector((state) => state.language);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 10;
  const dispatch = useDispatch();
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [couponToUpdate, setCouponToUpdate] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isWarnModalOpen, setIsWarnModalOpen] = useState(false);
  const [terms, setTerms] = useState([]);
  const [currentTerm, setCurrentTerm] = useState("");

  const couponsData = useSelector(
    (state) => state.admin.coupons?.data?.coupon || []
  );
  const totalCouponData = useSelector(
    (state) => state.admin.coupons?.data?.totalData || 0
  );

  useEffect(() => {
    setLoading(true);
    const b = {
      currentPage,
      filter,
    };
    dispatch(fetchCouponList(b)).then(() => {
      setLoading(false);
    });
  }, [dispatch, currentPage, filter]);

  const handleDeleteClick = (coupon) => {
    setCouponToDelete(coupon);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (couponToDelete) {
      setLoading(true);
      dispatch(deleteCoupon(couponToDelete._id))
        .then(() => {
          const b = {
            currentPage,
            filter,
          };
          dispatch(fetchCouponList(b));
          toast.success(translations.toaster.coupondeletedSuccessfully);
        })
        .catch((error) => {
          console.error("Delete failed", error);
        })
        .finally(() => {
          setLoading(false);
          setIsModalOpen(false);
          setCouponToDelete(null);
        });
    }
  };

  const handleUpdateClick = (coupon) => {
    setCouponToUpdate(coupon);
    setIsUpdateModalOpen(true);
    setTerms(coupon.termsAndConditions || []);
  };

  const {
    register: registerUpdate,
    handleSubmit: handleSubmitUpdate,
    reset,
    formState: { errors: errorsUpdate, isSubmitting: isSubmittingUpdate },
  } = useForm();

  const onSubmitUpdate = async (data) => {
    const payload = {
      _id: couponToUpdate._id,
      code: data.code,
      discountType: data.discountType,
      discountValue: parseInt(data.discountValue),
      maxDiscount: parseInt(data.maxDiscount),
      minBillAmount: parseInt(data.minBillAmount),
      validFrom: data.validFrom,
      validTo: data.validTo,
      promoType: data.promoType,
      usageLimit: parseInt(data.usageLimit),
      userLimit: parseInt(data.userLimit),
      excludedUsers: ["string"],
      usedBy: {},
      metadata: {},
      termsAndConditions: terms,
    };

    setLoading(true);
    dispatch(updateCoupon({ id: couponToUpdate._id, couponData: payload }))
      .then(() => {
        const b = {
          currentPage,
          filter,
        };
        dispatch(fetchCouponList(b));
        setLoading(false);
        toast.success(translations.toaster.couponupdatedSuccessfully);
      })
      .catch((error) => {
        console.error("Failed to update coupon", error);
        setLoading(false);
      })
      .finally(() => {
        setIsUpdateModalOpen(false);
        setCouponToUpdate(null);
        reset(); // Reset form fields
      });
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = async (data) => {
    if (terms.length === 0) {
      toast.error("Please add at least one term and condition.");
      return;
    }
    const payload = {
      code: data.code,
      discountType: data.discountType,
      discountValue: parseInt(data.discountValue),
      maxDiscount: parseInt(data.maxDiscount),
      minBillAmount: parseInt(data.minBillAmount),
      validFrom: data.validFrom,
      validTo: data.validTo,
      promoType: data.promoType,
      usageLimit: parseInt(data.usageLimit),
      userLimit: parseInt(data.userLimit),
      excludedUsers: ["string"],
      usedBy: {},
      metadata: {},
      termsAndConditions: terms,
    };

    setLoading(true);
    dispatch(addCoupon(payload))
      .then(() => {
        const b = {
          currentPage,
          filter,
        };
        dispatch(fetchCouponList(b));
        setLoading(false);
        toast.success(translations.toaster.couponaddedSuccessfully);
      })
      .catch((error) => {
        console.error("Failed to add coupon", error);
        setLoading(false);
      })
      .finally(() => {
        setIsAddModalOpen(false);
        reset();
        setTerms([]); // Reset form fields
      });
  };

  const statusCoupons = (couponId) => {
    setLoading(true);
    dispatch(updateCouponStatus(couponId))
      .then(() => {
        const b = { currentPage, filter };
        dispatch(fetchCouponList(b));
        setLoading(false);
        toast.success(translations.toaster.couponstatusupdatedsuccessfully);
      })
      .catch((error) => {
        console.error("Failed to update coupon status", error);
        setLoading(false);
        toast.error(translations.toaster.failedtoupdatecouponstatus);
      })
      .finally(() => {
        setIsWarnModalOpen(false);
        setCouponToUpdate(null);
      });
  };

  const [error, setError] = useState("");
  const handlePageChange = (event) => {
    const selectedPage = Number(event.target.value);

    if (selectedPage > totalPages) {
      setError(`Maximum value must be ${totalPages}`);
      // setCurrentPage(totalPages);
      return;
    }
    setCurrentPage(selectedPage);
    setError("");
  };
  const totalPages = Math.ceil(totalCouponData / itemsPerPage);

  return (
    <>
      <Layout>
        <div className="min-h-screen w-full px-4 py-4">
          <section className="pt-1 flex justify-between items-center">
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-2 rounded"
            >
              {translations.coupons.addCoupon}
            </button>

            <div className="text-md text-white flex items-center justify-end space-x-1 ">
              <div className="rounded-full bg-[#5A189A] py-2 px-3">
                <Link
                  to="/"
                  className=" text-white  no-underline cursor-pointer"
                >
                  {translations.coupons.dashboard}
                </Link>
                <span className="mx-1">/</span>
                <span className="text-green-300">
                  {translations.coupons.coupons}
                </span>
              </div>
            </div>
          </section>
          <div className="p-4 w-full bg-gray-800 shadow-xl rounded mt-3">
            {loading ? (
              <Loader />
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold text-white">
                    {translations.coupons.couponList}
                  </h2>
                  <div className="flex items-center">
                    <label htmlFor="coupon-status" className="text-white px-2">
                      {translations.coupons.filterbyStatus} :
                    </label>
                    <div className="relative">
                      <select
                        id="coupon-status"
                        className="bg-gray-700 text-white rounded ps-2 pe-5 py-2 focus:outline-none w-full appearance-none"
                        value={filter}
                        onChange={(e) => {
                          const value =
                            e.target.value === "all"
                              ? "all"
                              : Number(e.target.value);
                          setFilter(value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="all">{translations.coupons.all}</option>
                        <option value="501">
                          {translations.coupons.active}
                        </option>
                        <option value="502">
                          {translations.coupons.inactive}
                        </option>
                        <option value="503">
                          {translations.coupons.expired}
                        </option>
                      </select>

                      <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                {couponsData.length === 0 ? (
                  <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
                    <p className="text-xl">{translations.coupons.noData}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid  md:grid lg:grid  overflow-x-auto ">
                      <table className="min-w-full text-white shadow-md text-center  p-2">
                        <thead className="bg-[#5A189A] text-white text-md text-center">
                          <tr>
                            <th className=" px-3 py-3 whitespace-nowrap">
                              {translations.coupons.code}
                            </th>
                            <th className=" px-3 py-3 whitespace-nowrap">
                              {translations.coupons.expiredDate}
                            </th>
                            <th className=" px-3 py-3 whitespace-nowrap">
                              {translations.coupons.minimumAmount}
                            </th>
                            <th className=" px-3 py-3 whitespace-nowrap">
                              {translations.coupons.discount}
                            </th>

                            <th className=" px-3 py-3 whitespace-nowrap">
                              {translations.coupons.UsageCountUserLimit}
                            </th>
                            <th className=" px-3 py-3 whitespace-nowrap">
                              {translations.coupons.status}
                            </th>
                            <th className=" px-3 py-3 whitespace-nowrap">
                              {translations.coupons.actions}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-600 ">
                          {couponsData.map((coupon, index) => (
                            <tr
                              key={index}
                              className={`text-center mb-3 ${
                                coupon.isExpired === false
                                  ? "text-white"
                                  : "text-red-500"
                              }`}
                            >
                              <p>
                                <td className="px-3 py-3">{coupon.code}</td>
                              </p>
                              <td className="px-3 py-3">
                                <p>
                                  {" "}
                                  {new Date(
                                    coupon.validTo
                                  ).toLocaleDateString()}
                                </p>
                              </td>
                              <td className="px-3 py-3">
                                <p> {coupon.minBillAmount}</p>
                              </td>
                              <td className="px-3 py-3">
                                <p>
                                  {" "}
                                  {coupon.maxDiscount
                                    ? coupon.maxDiscount
                                    : "-"}
                                </p>
                              </td>

                              <td className="px-3 py-3  whitespace-nowrap">
                                <p>
                                  {" "}
                                  {coupon.usageCount} / {coupon.usageLimit}
                                </p>
                              </td>
                              <td
                                className={`px-4 py-3 font-semibold  ${
                                  coupon.isExpired === true
                                    ? "cursor"
                                    : "cursor-pointer"
                                }`}
                              >
                                <p
                                  onClick={() => {
                                    if (
                                      coupon.isExpired !== true &&
                                      new Date(coupon.validTo) > new Date()
                                    ) {
                                      setIsWarnModalOpen(true);
                                      setCouponToUpdate(coupon);
                                    } else {
                                      // console.log("Coupon is expired.");
                                    }
                                  }}
                                  className={`inline-flex rounded-full px-3.5 py-1 text-body-sm font-medium capitalize ${
                                    coupon.isActive === true
                                      ? "bg-[#219653]/[0.08] text-[#219653]"
                                      : new Date(coupon.validTo) > new Date() &&
                                        coupon.isActive === false &&
                                        coupon.isExpired === false
                                      ? "bg-[#FFA70B]/[0.08] text-[#FFA70B]"
                                      : "bg-[#D34053]/[0.08] text-[#D34053]"
                                  }`}
                                >
                                  {coupon.isActive === true
                                    ? translations.coupons.active
                                    : new Date(coupon.validTo) > new Date() &&
                                      coupon.isActive === false &&
                                      coupon.isExpired === false
                                    ? translations.coupons.inactive
                                    : translations.coupons.expired}
                                </p>
                              </td>
                              <td className="px-4 py-3 flex justify-center">
                                <button
                                  onClick={() => handleUpdateClick(coupon)}
                                  className="text-green-600 hover:text-green-300 px-2  text-xl"
                                >
                                  <FaRegEdit />
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(coupon)}
                                  className="text-red-700  hover:text-red-500  text-2xl"
                                >
                                  <MdDelete />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination Controls */}
                    <div className="mt-4 flex justify-between text-white">
                      <svg
                        className={`${
                          currentPage === 1
                            ? "fill-current"
                            : "cursor-pointer fill-current"
                        }
                        ${dir === "rtl" && "rotate-180"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320 512"
                        width={24}
                        height={24}
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        aria-disabled={currentPage === 1}
                      >
                        <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
                      </svg>
                      <span>
                        {translations.coupons.page}
                        {totalPages > 1 ? (
                          <input
                            type="number"
                            name=""
                            id="pagination-jump"
                            className={
                              error
                                ? `mx-2 rounded-md border  ps-1 border-red  bg-gray-800 text-white shadow-card focus:border-primary`
                                : `mx-2 rounded-md border border-gray-300 ps-1  bg-gray-800 text-white shadow-card focus:border-primary`
                            }
                            min={1}
                            max={totalPages}
                            defaultValue={currentPage}
                            onChange={(e) => e.target.value}
                            onBlur={(e) => handlePageChange(e)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handlePageChange(e);
                              }
                            }}
                          />
                        ) : (
                          <span className="mx-1">{currentPage}</span>
                        )}
                        {translations.coupons.of} {totalPages}
                      </span>
                      <svg
                        className={`${
                          currentPage === totalPages
                            ? "fill-current"
                            : "cursor-pointer fill-current"
                        }
                        ${dir === "rtl" && "rotate-180"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 320 512"
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(prev + 1, totalPages)
                          )
                        }
                        aria-disabled={currentPage === totalPages}
                      >
                        <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
                      </svg>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </Layout>
      {/* delete modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        width="w-[95%] sm:w-[90%] md:w-[80%] lg:w-[60%] xl:w-[40%] max-w-md"
      >
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 text-white mx-auto">
          <h2 className="text-xl font-semibold mb-4 text-red-600 text-center sm:text-left">
            {translations.coupons.confirmDelete}
          </h2>

          <p className="mb-6 text-center sm:text-left">
            {translations.coupons.warning}
          </p>

          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              className="w-full sm:w-auto bg-gray-500 px-4 py-2 rounded hover:bg-gray-400"
              onClick={() => setIsModalOpen(false)}
            >
              {translations.coupons.cancel}
            </button>
            <button
              className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              onClick={() => {
                confirmDelete();
                setIsModalOpen(false);
              }}
            >
              {translations.coupons.delete}
            </button>
          </div>
        </div>
      </Modal>

      {/* update modal */}
      <Modal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setCouponToUpdate(null);
          setTerms([]); // Reset terms
          reset(); // assuming you're using react-hook-form
        }}
        width="w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] max-w-6xl"
      >
        <div className="px-4 sm:px-6 md:px-8 py-3">
          <h3 className="text-xl text-center mb-6 text-white">
            {translations.coupons.updateDiscountCode}
          </h3>

          <form onSubmit={handleSubmitUpdate(onSubmitUpdate)} noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Discount Code */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.discountCode}
                </label>
                <input
                  type="text"
                  defaultValue={couponToUpdate?.code}
                  {...registerUpdate("code", { required: "Code is required" })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errorsUpdate.code && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.code.message}
                  </span>
                )}
              </div>

              {/* Discount Type */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.discountType}
                </label>
                <select
                  defaultValue={couponToUpdate?.discountType}
                  {...registerUpdate("discountType", {
                    required: "Discount type is required",
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                >
                  <option value="">{translations.coupons.select}</option>
                  <option value="PERCENTAGE">
                    {translations.coupons.percentage}
                  </option>
                  <option value="FIXED">{translations.coupons.fixed}</option>
                </select>
                {errorsUpdate.discountType && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.discountType.message}
                  </span>
                )}
              </div>

              {/* Promo Type */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.promoType}
                </label>
                <select
                  defaultValue={couponToUpdate?.promoType}
                  {...registerUpdate("promoType", {
                    required: "Promo type is required",
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                >
                  <option value="">{translations.coupons.select}</option>
                  <option value="ONE_TIME">
                    {translations.coupons.oneTime}
                  </option>
                  <option value="MULTIPLE_USE">
                    {translations.coupons.multipleUse}
                  </option>
                  <option value="NEW_USER">
                    {translations.coupons.newUser}
                  </option>
                  <option value="DAILY">{translations.coupons.daily}</option>
                  <option value="WEEKLY">{translations.coupons.weekly}</option>
                </select>
                {errorsUpdate.promoType && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.promoType.message}
                  </span>
                )}
              </div>

              {/* Valid From */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.validFrom}
                </label>
                <input
                  type="date"
                  defaultValue={couponToUpdate?.validFrom?.split("T")[0]}
                  {...registerUpdate("validFrom", {
                    required: "Valid from date is required",
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errorsUpdate.validFrom && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.validFrom.message}
                  </span>
                )}
              </div>

              {/* Valid To */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.validTo}
                </label>
                <input
                  type="date"
                  defaultValue={couponToUpdate?.validTo?.split("T")[0]}
                  {...registerUpdate("validTo", {
                    required: "Valid to date is required",
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errorsUpdate.validTo && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.validTo.message}
                  </span>
                )}
              </div>

              {/* Discount Value */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.discountValue}
                </label>
                <input
                  type="number"
                  defaultValue={couponToUpdate?.discountValue}
                  {...registerUpdate("discountValue", {
                    required: "Value is required",
                    min: { value: 1, message: "Value must be at least 1" },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errorsUpdate.discountValue && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.discountValue.message}
                  </span>
                )}
              </div>

              {/* Max Discount */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.maxDiscount}
                </label>
                <input
                  type="number"
                  defaultValue={couponToUpdate?.maxDiscount}
                  {...registerUpdate("maxDiscount", {
                    required: "Max discount is required",
                    min: {
                      value: 1,
                      message: "Max discount must be at least 1",
                    },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errorsUpdate.maxDiscount && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.maxDiscount.message}
                  </span>
                )}
              </div>

              {/* Min Bill Amount */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.minBillAmount}
                </label>
                <input
                  type="number"
                  defaultValue={couponToUpdate?.minBillAmount}
                  {...registerUpdate("minBillAmount", {
                    required: "Minimum bill is required",
                    min: {
                      value: 0,
                      message: "Minimum bill cannot be negative",
                    },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errorsUpdate.minBillAmount && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.minBillAmount.message}
                  </span>
                )}
              </div>

              {/* Usage Limit */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.usageLimit}
                </label>
                <input
                  type="number"
                  defaultValue={couponToUpdate?.usageLimit}
                  {...registerUpdate("usageLimit", {
                    required: "Usage limit required",
                    min: {
                      value: 0,
                      message: "Usage limit cannot be negative",
                    },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errorsUpdate.usageLimit && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.usageLimit.message}
                  </span>
                )}
              </div>

              {/* User Limit */}
              <div className="col-span-1">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.userLimit}
                </label>
                <input
                  type="number"
                  defaultValue={couponToUpdate?.userLimit}
                  {...registerUpdate("userLimit", {
                    required: "User limit required",
                    min: {
                      value: 0,
                      message: "User limit cannot be negative",
                    },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errorsUpdate.userLimit && (
                  <span className="text-sm text-red-500">
                    {errorsUpdate.userLimit.message}
                  </span>
                )}
              </div>

              {/* Terms and Conditions (wider) */}
              <div className="col-span-2">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.termsCondition}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentTerm}
                    onChange={(e) => setCurrentTerm(e.target.value)}
                    placeholder={translations.coupons.termPlaceholder}
                    className="flex-1 rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (currentTerm.trim()) {
                        setTerms((prev) => [
                          ...prev,
                          currentTerm.trim(),
                        ]);
                        setCurrentTerm("");
                      }
                    }}
                    className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                  >
                    {translations.coupons.add}
                  </button>
                </div>
                {terms.length > 0 && (
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-300 max-h-28 overflow-auto border border-gray-100 rounded-md p-2 h-[60px]">
                    {terms.map((term, index) => (
                      <li
                        key={index}
                        className="flex justify-between items-center gap-2 border-b"
                      >
                        <span className="break-all">{term}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setTerms((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="text-red-400 hover:text-red-600 text-xs"
                        >
                          {translations.coupons.remove}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsUpdateModalOpen(false);
                  setCouponToUpdate(null);
                  setTerms([]); // Reset terms
                  reset();
                }}
                className="w-full sm:w-auto px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-500"
              >
                {translations.coupons.cancel}
              </button>
              <button
                type="submit"
                disabled={isSubmittingUpdate}
                className="w-full sm:w-auto px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-500"
              >
                {isSubmittingUpdate
                  ? translations.coupons.updating
                  : translations.coupons.updateCoupon}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Add Coupon Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        width="w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] max-w-6xl"
      >
        <div className="px-4 sm:px-6 md:px-8 py-3">
          <h3 className="text-xl text-center mb-6 text-white">
            {translations.coupons.addNewDiscountCode}
          </h3>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Discount Code */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.discountCode}
                </label>
                <input
                  type="text"
                  placeholder="Enter discount code"
                  {...register("code", { required: "Code is required" })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.code && (
                  <span className="text-sm text-red-500">
                    {errors.code.message}
                  </span>
                )}
              </div>

              {/* Discount Type */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.discountType}
                </label>
                <select
                  {...register("discountType", {
                    required: "Discount type is required",
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                >
                  <option value="">
                    {translations.coupons.selectdiscounttype}
                  </option>
                  <option value="PERCENTAGE">
                    {translations.coupons.percentage}
                  </option>
                  <option value="FIXED">{translations.coupons.fixed}</option>
                </select>
                {errors.discountType && (
                  <span className="text-sm text-red-500">
                    {errors.discountType.message}
                  </span>
                )}
              </div>

              {/* Promo Type */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.promoType}
                </label>
                <select
                  {...register("promoType", {
                    required: "Promo type is required",
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                >
                  <option value="">
                    {translations.coupons.selectpromotype}
                  </option>
                  <option value="ONE_TIME">
                    {translations.coupons.oneTime}
                  </option>
                  <option value="MULTIPLE_USE">
                    {translations.coupons.multipleUse}
                  </option>
                  <option value="NEW_USER">
                    {translations.coupons.newUser}
                  </option>
                  <option value="DAILY">{translations.coupons.daily}</option>
                  <option value="WEEKLY">{translations.coupons.weekly}</option>
                </select>
                {errors.promoType && (
                  <span className="text-sm text-red-500">
                    {errors.promoType.message}
                  </span>
                )}
              </div>

              {/* Valid From */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.validFrom}
                </label>
                <input
                  type="date"
                  {...register("validFrom", {
                    required: "Valid from date is required",
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.validFrom && (
                  <span className="text-sm text-red-500">
                    {errors.validFrom.message}
                  </span>
                )}
              </div>

              {/* Valid To */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.validTo}
                </label>
                <input
                  type="date"
                  {...register("validTo", {
                    required: "Valid to date is required",
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.validTo && (
                  <span className="text-sm text-red-500">
                    {errors.validTo.message}
                  </span>
                )}
              </div>

              {/* Discount Value */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.discountValue}
                </label>
                <input
                  type="number"
                  placeholder={translations.coupons.enterdiscountvalue}
                  {...register("discountValue", {
                    required: "Value is required",
                    min: { value: 1, message: "Value must be at least 1" },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.discountValue && (
                  <span className="text-sm text-red-500">
                    {errors.discountValue.message}
                  </span>
                )}
              </div>

              {/* Max Discount */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.maxDiscount}
                </label>
                <input
                  type="number"
                  placeholder={translations.coupons.entermaxdiscount}
                  {...register("maxDiscount", {
                    required: "Max discount is required",
                    min: {
                      value: 1,
                      message: "Max discount must be at least 1",
                    },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.maxDiscount && (
                  <span className="text-sm text-red-500">
                    {errors.maxDiscount.message}
                  </span>
                )}
              </div>

              {/* Min Bill Amount */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.minBillAmount}
                </label>
                <input
                  type="number"
                  placeholder={translations.coupons.enterminimumbillamount}
                  {...register("minBillAmount", {
                    required: "Minimum bill is required",
                    min: {
                      value: 0,
                      message: "Minimum bill cannot be negative",
                    },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.minBillAmount && (
                  <span className="text-sm text-red-500">
                    {errors.minBillAmount.message}
                  </span>
                )}
              </div>

              {/* Usage Limit */}
              <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.usageLimit}
                </label>
                <input
                  type="number"
                  placeholder={translations.coupons.enterusagelimit}
                  {...register("usageLimit", {
                    required: "Usage limit required",
                    min: {
                      value: 0,
                      message: "Usage limit cannot be negative",
                    },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.usageLimit && (
                  <span className="text-sm text-red-500">
                    {errors.usageLimit.message}
                  </span>
                )}
              </div>
              {/* <div className="col-span-full mb-2">
                <label className="block text-gray-400 text-sm mb-1">
                  Terms And Conditions
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={currentTerm}
                    onChange={(e) => setCurrentTerm(e.target.value)}
                    placeholder="Enter a term and condition"
                    className="flex-1 rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (currentTerm.trim()) {
                        setTerms((prev) => [...prev, currentTerm.trim()]);
                        setCurrentTerm("");
                      }
                    }}
                    className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>

                {terms.length > 0 && (
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
                    {terms.map((term, index) => (
                      <li
                        key={index}
                        className="flex justify-between items-center gap-2"
                      >
                        <span>{index+1} - {term}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setTerms((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="text-red-400 hover:text-red-600 text-xs"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div> */}

              {/* User Limit */}
              {/* <div className="mb-3">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.userLimit}
                </label>
                <input
                  type="number"
                  placeholder={translations.coupons.enteruserlimit}
                  {...register("userLimit", {
                    required: "User limit required",
                    min: { value: 0, message: "User limit cannot be negative" },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.userLimit && (
                  <span className="text-sm text-red-500">
                    {errors.userLimit.message}
                  </span>
                )}
              </div> */}

              <div className="col-span-1">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.userLimit}
                </label>
                <input
                  type="number"
                  placeholder={translations.coupons.enteruserlimit}
                  {...register("userLimit", {
                    required: "User limit required",
                    min: { value: 0, message: "User limit cannot be negative" },
                  })}
                  className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                />
                {errors.userLimit && (
                  <span className="text-sm text-red-500">
                    {errors.userLimit.message}
                  </span>
                )}
              </div>

              {/* Terms and Conditions (wider) */}
              <div className="col-span-2">
                <label className="block text-gray-400 text-sm mb-1">
                  {translations.coupons.termsCondition}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentTerm}
                    onChange={(e) => setCurrentTerm(e.target.value)}
                    placeholder={translations.coupons.termPlaceholder}
                    className="flex-1 rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (currentTerm.trim()) {
                        setTerms((prev) => [...prev, currentTerm.trim()]);
                        setCurrentTerm("");
                      }
                    }}
                    className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                  >
                    {translations.coupons.add}
                  </button>
                </div>
                {terms.length > 0 && (
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-gray-300 max-h-28 overflow-auto border border-gray-100 rounded-md p-2 h-[60px]">
                    {terms.map((term, index) => (
                      <li
                        key={index}
                        className="flex justify-between items-center gap-2 border-b"
                      >
                        <span className="break-all">{term}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setTerms((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="text-red-400 hover:text-red-600 text-xs"
                        >
                          {translations.coupons.remove}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {setIsAddModalOpen(false); setTerms([])}}
                className="w-full sm:w-auto px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-500"
              >
                {translations.coupons.cancel}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-500"
              >
                {isSubmitting
                  ? translations.coupons.adding
                  : translations.coupons.addCoupon}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* update coupon status modal */}
      <Modal
        isOpen={isWarnModalOpen}
        onClose={() => {
          setIsWarnModalOpen(false);
          setCouponToUpdate(null);
        }}
        width="w-[450px]"
      >
        <div className="p-6">
          <h2 className="mb-6 text-xl font-bold text-white">
            {translations.couponStatusModal.confirm_status_change}
          </h2>

          <div className="mb-4 rounded-md border border-yellow-300 bg-yellow-200 p-4 text-yellow-800">
            {couponToUpdate?.isActive === true ? (
              <>
                <strong>{translations.couponStatusModal.warning}:</strong>{" "}
                {translations.couponStatusModal.inactive_warning}
              </>
            ) : (
              <>
                <strong>{translations.couponStatusModal.warning}:</strong>{" "}
                {translations.couponStatusModal.active_warning}
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 text-end">
            <button
              onClick={() =>
                statusCoupons(couponToUpdate ? couponToUpdate._id : "")
              }
              className="basis-1/4 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
            >
              {translations.couponStatusModal.yes}
            </button>

            <button
              onClick={() => {
                setIsWarnModalOpen(false);
                setCouponToUpdate(null);
              }}
              className="basis-1/4 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              {translations.couponStatusModal.no}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default Coupons;
